import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { PageEmptyState, PageHeader, PageListContainer } from "../../../components/Page";
import { PageSectionCard } from "../../../components/layout";
import { AppAutocomplete, AppButton, AppTextField } from "../../../components/form";
import AppIconButton from "../../../components/form/AppIconButton";
import ResponseRenderer from "../../report/ui/ResponseRenderer";
import {
  adminDataAdaptorService,
  isWarehouseAdaptorType,
  type DataAdaptor,
} from "../../../services/adminDataAdaptorService";
import {
  SEMANTIC_GRAINS,
  emptySemanticModel,
  semanticModelService,
  type SemanticModel,
  type SemanticPreview,
} from "../../../services/semanticModelService";
import { useDeleteConfirm } from "../../../hooks/useDeleteConfirm";

type Section = "tables" | "metrics" | "dimensions" | "joins";

type Column = {
  key: string;
  labelKey: string;
  width?: number | string;
  /** Comma-separated text edited as a string list. */
  list?: boolean;
  options?: string[];
  mono?: boolean;
};

const COLUMNS: Record<Section, Column[]> = {
  tables: [
    { key: "name", labelKey: "semanticModels.col.alias", width: 140 },
    { key: "table", labelKey: "semanticModels.col.physicalTable", width: 220, mono: true },
    { key: "timeColumn", labelKey: "semanticModels.col.timeColumn", width: 150, mono: true },
    { key: "description", labelKey: "semanticModels.col.description" },
  ],
  metrics: [
    { key: "name", labelKey: "semanticModels.col.name", width: 150 },
    { key: "label", labelKey: "semanticModels.col.label", width: 150 },
    { key: "table", labelKey: "semanticModels.col.table", width: 130 },
    { key: "expression", labelKey: "semanticModels.col.expression", width: 240, mono: true },
    { key: "filter", labelKey: "semanticModels.col.filter", width: 200, mono: true },
    { key: "synonyms", labelKey: "semanticModels.col.synonyms", list: true },
  ],
  dimensions: [
    { key: "name", labelKey: "semanticModels.col.name", width: 150 },
    { key: "label", labelKey: "semanticModels.col.label", width: 150 },
    { key: "table", labelKey: "semanticModels.col.table", width: 130 },
    { key: "expression", labelKey: "semanticModels.col.expression", width: 220, mono: true },
    { key: "type", labelKey: "semanticModels.col.type", width: 120, options: ["STRING", "NUMBER", "TIME", "BOOLEAN"] },
    { key: "synonyms", labelKey: "semanticModels.col.synonyms", list: true },
  ],
  joins: [
    { key: "from", labelKey: "semanticModels.col.fromMany", width: 150 },
    { key: "to", labelKey: "semanticModels.col.toOne", width: 150 },
    { key: "on", labelKey: "semanticModels.col.on", mono: true },
    { key: "type", labelKey: "semanticModels.col.joinType", width: 120, options: ["LEFT", "INNER"] },
  ],
};

const NEW_ROW: Record<Section, () => Record<string, unknown>> = {
  tables: () => ({ name: "", table: "" }),
  metrics: () => ({ name: "", table: "", expression: "COUNT(*)" }),
  dimensions: () => ({ name: "", table: "", expression: "", type: "STRING" }),
  joins: () => ({ from: "", to: "", on: "", type: "LEFT" }),
};

function errorText(e: unknown): string {
  const anyErr = e as { response?: { data?: { message?: string; error?: string } }; message?: string };
  return anyErr?.response?.data?.message || anyErr?.response?.data?.error || anyErr?.message || String(e);
}

function notify(title: string, content: string, color: "success" | "error" = "success") {
  window.dispatchEvent(
    new CustomEvent("showGlobalNotification", {
      detail: { color, icon: color === "success" ? "check" : "warning", title, content },
    })
  );
}

/** One editable grid per model section; rows are plain objects so all four sections share it. */
function EditableRows({
  section,
  rows,
  tableAliases,
  onChange,
}: {
  section: Section;
  rows: Record<string, unknown>[];
  tableAliases: string[];
  onChange: (rows: Record<string, unknown>[]) => void;
}) {
  const { t } = useTranslation();
  const columns = COLUMNS[section];
  const update = (index: number, key: string, value: unknown) => {
    onChange(rows.map((row, i) => (i === index ? { ...row, [key]: value } : row)));
  };
  const optionsFor = (column: Column) =>
    column.options ?? (["table", "from", "to"].includes(column.key) && section !== "tables" ? tableAliases : undefined);

  return (
    <Box>
      <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.key} sx={{ minWidth: column.width }}>
                  {t(column.labelKey)}
                </TableCell>
              ))}
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                {columns.map((column) => {
                  const options = optionsFor(column);
                  const raw = row[column.key];
                  const value = column.list && Array.isArray(raw) ? (raw as string[]).join(", ") : (raw as string) ?? "";
                  return (
                    <TableCell key={column.key} sx={{ minWidth: column.width, verticalAlign: "top" }}>
                      {options ? (
                        <AppAutocomplete
                          label=""
                          options={options}
                          value={value || null}
                          onChange={(_, next) => update(index, column.key, next || "")}
                          renderInput={(params) => (
                            <AppTextField {...params} hiddenLabel size="small" />
                          )}
                        />
                      ) : (
                        <AppTextField
                          value={value}
                          multiline={column.mono}
                          maxRows={4}
                          inputProps={{ style: column.mono ? { fontFamily: "monospace", fontSize: 12 } : undefined }}
                          onChange={(e) =>
                            update(
                              index,
                              column.key,
                              column.list
                                ? e.target.value
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean)
                                : e.target.value
                            )
                          }
                        />
                      )}
                    </TableCell>
                  );
                })}
                <TableCell align="right" sx={{ verticalAlign: "top" }}>
                  <AppIconButton size="small" onClick={() => onChange(rows.filter((_, i) => i !== index))}>
                    <DeleteOutlineOutlinedIcon fontSize="small" />
                  </AppIconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <AppButton sx={{ mt: 1 }} size="small" startIcon={<AddIcon />} onClick={() => onChange([...rows, NEW_ROW[section]()])}>
        {t("semanticModels.addRow")}
      </AppButton>
    </Box>
  );
}

/**
 * Semantic layer editor for warehouse adaptors: metrics, dimensions and join paths (AI suggestion → edit → approve),
 * a query preview through the compiler, and rollup Prep jobs for dashboard speed.
 */
export default function SemanticModelsPage() {
  const { t } = useTranslation();
  const { confirmDelete, isCancelled } = useDeleteConfirm();
  const navigate = useNavigate();
  const [adaptors, setAdaptors] = useState<DataAdaptor[]>([]);
  const [adaptorId, setAdaptorId] = useState<string>("");
  const [model, setModel] = useState<SemanticModel | null>(null);
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Section | "preview" | "rollups">("metrics");

  const [previewMetrics, setPreviewMetrics] = useState<string[]>([]);
  const [previewDims, setPreviewDims] = useState<string[]>([]);
  const [previewGrain, setPreviewGrain] = useState<string>("");
  const [previewLast, setPreviewLast] = useState<string>("30d");
  const [preview, setPreview] = useState<SemanticPreview | null>(null);

  const [rollupGrain, setRollupGrain] = useState<string>("day");
  const [rollupMetrics, setRollupMetrics] = useState<string[]>([]);
  const [rollupDims, setRollupDims] = useState<string[]>([]);
  const [rollupLookback, setRollupLookback] = useState<string>("");

  useEffect(() => {
    adminDataAdaptorService
      .list()
      .then((list) => {
        const warehouses = (list || []).filter((a) => isWarehouseAdaptorType(a.type) && a.enabled);
        setAdaptors(warehouses);
        if (warehouses.length > 0) setAdaptorId((current) => current || warehouses[0].id);
      })
      .catch((e) => setError(errorText(e)))
      .finally(() => setLoading(false));
  }, []);

  const load = useCallback(async (id: string) => {
    if (!id) return;
    setLoading(true);
    setError(null);
    setPreview(null);
    try {
      const existing = await semanticModelService.byAdaptor(id);
      setModel(existing ?? emptySemanticModel(id));
      setDirty(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(adaptorId);
  }, [adaptorId, load]);

  const tableAliases = useMemo(() => (model?.tables || []).map((tb) => tb.name).filter(Boolean), [model]);
  const metricNames = useMemo(() => (model?.metrics || []).map((m) => m.name).filter(Boolean), [model]);
  const dimensionNames = useMemo(() => (model?.dimensions || []).map((d) => d.name).filter(Boolean), [model]);

  const setSection = (section: Section, rows: Record<string, unknown>[]) => {
    if (!model) return;
    setModel({ ...model, [section]: rows } as SemanticModel);
    setDirty(true);
  };

  const run = async (key: string, action: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await action();
    } catch (e) {
      if (!isCancelled(e)) setError(errorText(e));
    } finally {
      setBusy(null);
    }
  };

  const suggest = () =>
    run("suggest", async () => {
      const proposal = await semanticModelService.suggest(adaptorId);
      setModel({ ...proposal, id: model?.id, rollups: model?.rollups, status: "DRAFT" });
      setDirty(true);
      notify(t("semanticModels.title"), t("semanticModels.suggested", { source: proposal.source || "AI" }));
    });

  const save = () =>
    run("save", async () => {
      if (!model) return;
      const saved = await semanticModelService.save({ ...model, adaptorId });
      setModel(saved);
      setDirty(false);
      notify(t("semanticModels.title"), t("semanticModels.saved"));
    });

  const approve = () =>
    run("approve", async () => {
      if (!model?.id) return;
      setModel(await semanticModelService.approve(model.id));
      notify(t("semanticModels.title"), t("semanticModels.approved"));
    });

  const remove = () =>
    run("delete", async () => {
      if (!model?.id) return;
      await confirmDelete({ itemName: model.name || model.adaptorName || model.id });
      await semanticModelService.remove(model.id);
      setModel(emptySemanticModel(adaptorId));
      setDirty(false);
    });

  const runPreview = () =>
    run("preview", async () => {
      if (!model?.id) return;
      setPreview(
        await semanticModelService.preview(model.id, {
          metrics: previewMetrics,
          dimensions: previewDims,
          grain: previewGrain || undefined,
          timeRange: previewGrain && previewLast ? { last: previewLast } : undefined,
          limit: 50,
        })
      );
    });

  const createRollup = () =>
    run("rollup", async () => {
      if (!model?.id) return;
      const rollup = await semanticModelService.createRollup(model.id, {
        grain: rollupGrain,
        metrics: rollupMetrics,
        dimensions: rollupDims,
        lookback: rollupLookback || undefined,
      });
      setModel({ ...model, rollups: [...(model.rollups || []), rollup] });
      notify(t("semanticModels.title"), t("semanticModels.rollupCreated", { index: rollup.index }));
    });

  const statusChip = model?.id ? (
    <Chip
      size="small"
      color={model.status === "APPROVED" && !dirty ? "success" : "warning"}
      label={dirty ? t("semanticModels.unsaved") : t(`semanticModels.status.${model.status || "DRAFT"}`)}
    />
  ) : (
    <Chip size="small" label={t("semanticModels.notCreated")} />
  );

  return (
    <PageListContainer>
      <PageHeader
        title={t("semanticModels.title")}
        subtitle={t("semanticModels.subtitle")}
        actions={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <AppButton
              variant="outlined"
              startIcon={<AutoAwesomeRoundedIcon />}
              disabled={!adaptorId || !!busy}
              onClick={() => void suggest()}
            >
              {busy === "suggest" ? t("semanticModels.suggesting") : t("semanticModels.suggest")}
            </AppButton>
            <AppButton variant="contained" disabled={!model || !dirty || !!busy} onClick={() => void save()}>
              {t("common.save")}
            </AppButton>
            <AppButton
              variant="contained"
              color="success"
              disabled={!model?.id || dirty || model.status === "APPROVED" || !!busy}
              onClick={() => void approve()}
            >
              {t("semanticModels.approve")}
            </AppButton>
            {model?.id && (
              <AppButton variant="outlined" color="error" disabled={!!busy} onClick={() => void remove()}>
                {t("common.delete")}
              </AppButton>
            )}
          </Stack>
        }
      />

      {error ? (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      <PageSectionCard
        title={t("semanticModels.adaptor")}
        actions={statusChip}
      >
        {adaptors.length === 0 && !loading ? (
          <PageEmptyState message={t("semanticModels.noWarehouseAdaptors")} />
        ) : (
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
            <Box sx={{ minWidth: 280 }}>
              <AppAutocomplete
                label={t("semanticModels.adaptor")}
                options={adaptors}
                value={adaptors.find((a) => a.id === adaptorId) ?? null}
                getOptionLabel={(a) => `${a.name} · ${a.type}`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                onChange={(_, next) => {
                  if (!next) return;
                  if (dirty && !window.confirm(t("semanticModels.discardChanges"))) return;
                  setAdaptorId(next.id);
                }}
              />
            </Box>
            {model?.approvedAt && model.status === "APPROVED" && (
              <Typography variant="body2" color="text.secondary">
                {t("semanticModels.approvedBy", { user: model.approvedBy || "", date: new Date(model.approvedAt).toLocaleString() })}
              </Typography>
            )}
          </Stack>
        )}
      </PageSectionCard>

      {loading ? (
        <Stack alignItems="center" py={5}>
          <CircularProgress size={32} />
        </Stack>
      ) : model ? (
        <PageSectionCard title={model.name || model.adaptorName || t("semanticModels.model")}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 2 }}>
            <Tab value="metrics" label={`${t("semanticModels.tab.metrics")} (${model.metrics.length})`} />
            <Tab value="dimensions" label={`${t("semanticModels.tab.dimensions")} (${model.dimensions.length})`} />
            <Tab value="tables" label={`${t("semanticModels.tab.tables")} (${model.tables.length})`} />
            <Tab value="joins" label={`${t("semanticModels.tab.joins")} (${model.joins.length})`} />
            <Tab value="preview" label={t("semanticModels.tab.preview")} />
            <Tab value="rollups" label={`${t("semanticModels.tab.rollups")} (${model.rollups?.length ?? 0})`} />
          </Tabs>

          {(tab === "metrics" || tab === "dimensions" || tab === "tables" || tab === "joins") && (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {t(`semanticModels.help.${tab}`)}
              </Typography>
              <EditableRows
                section={tab}
                rows={(model[tab] as unknown as Record<string, unknown>[]) || []}
                tableAliases={tableAliases}
                onChange={(rows) => setSection(tab, rows)}
              />
            </>
          )}

          {tab === "preview" && (
            <Stack spacing={2}>
              {!model.id || dirty ? (
                <Alert severity="info">{t("semanticModels.saveFirst")}</Alert>
              ) : null}
              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <AppAutocomplete
                  multiple
                  label={t("semanticModels.tab.metrics")}
                  options={metricNames}
                  value={previewMetrics}
                  onChange={(_, v) => setPreviewMetrics(v as string[])}
                  sx={{ minWidth: 240, flex: 1 }}
                />
                <AppAutocomplete
                  multiple
                  label={t("semanticModels.tab.dimensions")}
                  options={dimensionNames}
                  value={previewDims}
                  onChange={(_, v) => setPreviewDims(v as string[])}
                  sx={{ minWidth: 240, flex: 1 }}
                />
                <Box sx={{ minWidth: 140 }}>
                  <AppAutocomplete
                    label={t("semanticModels.grain")}
                    options={["", ...SEMANTIC_GRAINS]}
                    value={previewGrain}
                    getOptionLabel={(g) => (g ? g : t("semanticModels.noGrain"))}
                    onChange={(_, next) => setPreviewGrain(next ?? "")}
                  />
                </Box>
                <Box sx={{ minWidth: 120 }}>
                  <AppTextField
                    label={t("semanticModels.last")}
                    value={previewLast}
                    disabled={!previewGrain}
                    onChange={(e) => setPreviewLast(e.target.value)}
                  />
                </Box>
                <AppButton
                  variant="contained"
                  disabled={!model.id || dirty || !!busy || (previewMetrics.length === 0 && previewDims.length === 0)}
                  onClick={() => void runPreview()}
                >
                  {busy === "preview" ? t("semanticModels.running") : t("semanticModels.run")}
                </AppButton>
              </Stack>
              {preview && (
                <>
                  <Box
                    component="pre"
                    sx={{ m: 0, p: 1, fontSize: 12, overflowX: "auto", bgcolor: "action.hover", borderRadius: 1 }}
                  >
                    {preview.sql}
                  </Box>
                  <ResponseRenderer
                    payload={{
                      correlationId: "",
                      type: "table",
                      result: { columns: preview.columns, rows: preview.rows },
                      timestamp: new Date().toISOString(),
                    }}
                  />
                </>
              )}
            </Stack>
          )}

          {tab === "rollups" && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                {t("semanticModels.help.rollups")}
              </Typography>
              {(model.rollups || []).length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("semanticModels.col.index")}</TableCell>
                      <TableCell>{t("semanticModels.grain")}</TableCell>
                      <TableCell>{t("semanticModels.tab.metrics")}</TableCell>
                      <TableCell>{t("semanticModels.tab.dimensions")}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(model.rollups || []).map((r) => (
                      <TableRow key={`${r.index}-${r.prepJobId}`}>
                        <TableCell sx={{ fontFamily: "monospace" }}>{r.index}</TableCell>
                        <TableCell>{r.grain}</TableCell>
                        <TableCell>{(r.metrics || []).join(", ")}</TableCell>
                        <TableCell>{(r.dimensions || []).join(", ") || "—"}</TableCell>
                        <TableCell align="right">
                          <AppButton
                            size="small"
                            onClick={() => navigate(r.prepJobId ? `/data-prep/${r.prepJobId}/design` : "/data-prep")}
                          >
                            {t("semanticModels.openPrep")}
                          </AppButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {model.status !== "APPROVED" || dirty ? (
                <Alert severity="info">{t("semanticModels.approveFirst")}</Alert>
              ) : (
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <Box sx={{ minWidth: 140 }}>
                    <AppAutocomplete
                      label={t("semanticModels.grain")}
                      options={[...SEMANTIC_GRAINS]}
                      value={rollupGrain}
                      disableClearable
                      onChange={(_, next) => next && setRollupGrain(next)}
                    />
                  </Box>
                  <AppAutocomplete
                    multiple
                    label={t("semanticModels.tab.metrics")}
                    options={metricNames}
                    value={rollupMetrics}
                    onChange={(_, v) => setRollupMetrics(v as string[])}
                    sx={{ minWidth: 240, flex: 1 }}
                  />
                  <AppAutocomplete
                    multiple
                    label={t("semanticModels.tab.dimensions")}
                    options={dimensionNames}
                    value={rollupDims}
                    onChange={(_, v) => setRollupDims(v as string[])}
                    sx={{ minWidth: 240, flex: 1 }}
                  />
                  <Box sx={{ minWidth: 140 }}>
                    <AppTextField
                      label={t("semanticModels.lookback")}
                      placeholder="400d"
                      value={rollupLookback}
                      onChange={(e) => setRollupLookback(e.target.value)}
                    />
                  </Box>
                  <AppButton
                    variant="contained"
                    disabled={rollupMetrics.length === 0 || !!busy}
                    onClick={() => void createRollup()}
                  >
                    {t("semanticModels.createRollup")}
                  </AppButton>
                </Stack>
              )}
            </Stack>
          )}
        </PageSectionCard>
      ) : null}
    </PageListContainer>
  );
}
